import { isNotNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { Point } from '../../models/point.interface';
import { withoutUndoManager } from '../../utils/without-undo-manager';
import { MOUSE_CAPTURING_TOOL_NAME } from './config';

export class MouseCapturingTool extends go.Tool {
  constructor() {
    super();
    this.name = MOUSE_CAPTURING_TOOL_NAME;
    this.isEnabled = false;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.doDeactivate = this.doDeactivate.bind(this);
  }

  private mouseDownAction: ((point: Point) => void) | null = null;
  private oldDefaultCursor?: {
    diagram: string;
    document: string;
  };
  private overlay?: HTMLDivElement;
  cursor = '';
  allowedClickClass = '';

  startManually(mouseDownAction: (point: Point) => void) {
    this.diagram.currentTool = this;
    this.mouseDownAction = mouseDownAction;
    this.doActivate();
  }

  doActivate() {
    super.doActivate();
    withoutUndoManager(this.diagram, () => {
      this.diagram.isMouseCaptured = true;
      this.oldDefaultCursor = {
        diagram: this.diagram.defaultCursor,
        document: document.body.style.cursor,
      };
      this.diagram.defaultCursor = this.cursor;
      document.body.style.cursor = this.cursor;
      this.addOverlay();
      document.addEventListener('keydown', this.onKeyDown);
    });
  }

  doDeactivate() {
    super.doDeactivate();
    withoutUndoManager(this.diagram, () => {
      if (this.oldDefaultCursor) {
        document.body.style.cursor = this.oldDefaultCursor.document;
        this.diagram.defaultCursor = this.oldDefaultCursor.diagram;
      }
      this.diagram.isMouseCaptured = false;
      this.mouseDownAction = null;
      this.removeOverlay();
      document.removeEventListener('keydown', this.onKeyDown);
    });
  }

  doMouseDown() {
    super.doMouseDown();

    if (this.mouseDownAction && this.isAllowedClick()) {
      this.mouseDownAction(this.diagram.lastInput.documentPoint);
    }
  }

  private isAllowedClick() {
    const { div, lastInput } = this.diagram;
    const clickedOnCanvas = lastInput?.event?.target instanceof Node && div?.contains(lastInput.event.target);
    const clickedOnAllowedClass =
      isNotNullish(this.allowedClickClass) &&
      lastInput.event?.target instanceof Element &&
      lastInput.event.target.classList.contains(this.allowedClickClass);

    return clickedOnCanvas || clickedOnAllowedClass;
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.doDeactivate();
    }
  }

  private addOverlay() {
    if (!this.overlay) {
      this.overlay = document.createElement('div');
      this.overlay.style.width = '100%';
      this.overlay.style.height = '100%';
      this.overlay.style.zIndex = '99999';
      this.overlay.style.position = 'fixed';
      this.overlay.style.top = '0';
      this.overlay.style.left = '0';
    }

    this.overlay.addEventListener('click', this.doDeactivate);
    this.diagram.div?.appendChild(this.overlay);
  }

  private removeOverlay() {
    this.overlay?.removeEventListener('click', this.doDeactivate);
    if (this.overlay?.parentElement === this.diagram.div) {
      this.diagram.div?.removeChild(this.overlay);
    } else if (this.overlay?.remove) {
      this.overlay.remove();
    }
  }
}
